// =========================
// POSTER COMPONENT
// =========================
// External Libraries
import { LitElement, html } from 'lit';

// App Modules
import Auth from '../Auth';
import App from '../App';
import Utils from '../Utils';

// This is the PopinPoster class that handles the post creation
// It includes methods for rendering the post creation form and handling user interactions
class PopinPoster extends LitElement {
  static properties = {
    previews: { type: Array },
    files: { type: Array },
    placeholderText: { type: String },
  };

  constructor() {
    super();
    this.previews = [];
    this.files = [];
    this.placeholderText = '';
  }

  createRenderRoot() {
    return this;
  }

  // LIFECYCLE METHODS

  // This method is called when the component is first updated
  async firstUpdated() {
    this.user = Auth.currentUser;
    this.requestUpdate();
  }

  // This method is called when the component is connected to the DOM
  connectedCallback() {
    super.connectedCallback();
    this.updatePlaceholder();
    window.addEventListener('resize', this.updatePlaceholder.bind(this));
  }

  // This method is called when the component is disconnected from the DOM
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.updatePlaceholder.bind(this));
  }

  // UI HANDLERS

  // This method updates the placeholder text based on the screen size
  updatePlaceholder() {
    const isMobile = window.innerWidth <= 768;
    this.placeholderText = isMobile
      ? 'What’s up?'
      : 'PopIn and tell your friends what’s up!';
  }

  // This method handles the file input change event
  handleFileChange(e) {
    const newFiles = Array.from(e.target.files);
    const newPreviews = newFiles.map((file) => ({
      url: URL.createObjectURL(file),
      name: file.name,
    }));

    this.files = [...this.files, ...newFiles];
    this.previews = [...this.previews, ...newPreviews];

    e.target.value = '';
  }

  // This method removes an image from the preview and file arrays
  removeImage(index) {
    this.files.splice(index, 1);
    this.previews.splice(index, 1);
    this.files = [...this.files];
    this.previews = [...this.previews];
  }

  // POST HANDLERS

  // This method handles the full post submission
  async handleFullPostSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData();
    formData.append('text', form.text.value);

    this.files.forEach((file) => {
      formData.append('images', file);
    });

    try {
      const res = await fetch(`${App.apiBase}/post`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });

      if (!res.ok) throw new Error('Failed to post');

      form.reset();
      this.files = [];
      this.previews = [];
      document.querySelector('#postDialog').hide();
      window.dispatchEvent(new CustomEvent('postCreated'));
    } catch (err) {
      console.error('Error posting full form', err);
    }
  }

  // This method handles the quick post submission
  handleQuickPost(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const text = e.target.value.trim();
      if (!text) return;

      fetch(`${App.apiBase}/post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ text }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('Failed to post');
          e.target.value = '';
          window.dispatchEvent(new CustomEvent('postCreated'));
        })
        .catch((err) => console.error('Error posting quick post', err));
    }
  }

  render() {
    const profileImage = Utils.getProfileImage(this.user);
    const displayName = Utils.getDisplayName(this.user);

    return html`
      <style>
        #feed-poster {
          background: var(--sl-color-primary-100);
          display: flex;
          align-items: center;
          height: 100px;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.06);
          margin-bottom: 2rem;
        }

        .pfp-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          margin-right: 1rem;
        }

        .input-group {
          background-color: var(--white);
          border-radius: 8px;
          width: 100%;
          height: 100%;
          margin: 0px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        sl-input::part(base) {
          background-color: var(--white);
          border: none;
          margin-left: 1rem;
        }

        sl-input {
          width: 90%;
        }

        sl-icon-button {
          padding-right: 1%;
          font-size: 24px;
        }

        sl-dialog::part(body) {
          padding: 1rem;
          background-color: var(--white);
          border-radius: 12px;
          max-width: 600px;
        }

        sl-dialog::part(title) {
          font-family: var(--heading-font-family);
          font-weight: 700;
        }

        .dialog-user {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .dialog-pfp {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
        }

        .user-name {
          margin: 0;
          font-weight: bold;
        }

        .styled-textarea {
          width: 100%;
          min-height: 100px;
          font-size: 1rem;
          font-family: var(--base-font-family);
          padding: 0.5rem;
          border: 1px solid var(--standard-border);
          border-radius: 8px;
          resize: vertical;
          outline: none;
          margin-bottom: 1rem;
        }

        .preview-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 1rem;
        }

        .preview-thumb {
          position: relative;
        }

        .preview-thumb img {
          width: 100px;
          height: 100px;
          object-fit: cover;
          border-radius: 8px;
        }

        .remove-button {
          position: absolute;
          top: -8px;
          right: -8px;
          background: var(--white);
          border-radius: 50%;
          font-size: 0.8rem;
          cursor: pointer;
          padding: 2px 5px;
        }

        .hidden {
          display: none;
        }

        .footer-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: var(--sl-color-primary-100);
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .footer-actions p {
          margin: 0;
          font-weight: 500;
        }

        .action-icons {
          display: flex;
          align-items: center;
        }

        .action-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 1.25rem;
          margin-left: 0.75rem;
          cursor: pointer;
        }

        .action-icon:hover {
          color: var(--sl-color-primary-700);
        }

        .disabled-icon {
          color: var(--disabled-color);
        }

        .disabled-icon:hover {
          color: var(--disabled-color);
        }

        .post-button {
          font-weight: bold;
          width: 100%;
        }

        @media all and (max-width: 1366px) {
        }

        @media all and (max-width: 1024px) {
        }

        @media all and (max-width: 768px) {
          #feed-poster {
            padding: 0.75rem;
            margin-left: 50px;
            height: auto;
            .pfp-icon {
              margin-right: 0.5rem;
            }
            .input-group {
              width: calc(100% - 48px);
            }
            .input-group sl-input::part(base) {
              margin: 0px;
            }
          }
        }

        @media all and (max-width: 576px) {
          sl-input {
            width: 80%;
          }
        }
      </style>

      <div id="feed-poster">
        <img class="pfp-icon" src="${profileImage}" alt="User" />
        <div class="input-group">
          <sl-input
            placeholder="${this.placeholderText}"
            @keypress=${this.handleQuickPost}
          ></sl-input>
          <sl-icon-button
            name="camera-fill"
            label="Add Images"
            @click=${() => document.querySelector('#postDialog').show()}
          ></sl-icon-button>
        </div>
      </div>

      <sl-dialog id="postDialog" label="Create post">
        <form id="fullPostForm" @submit=${this.handleFullPostSubmit}>
          <div class="dialog-user">
            <img class="dialog-pfp" src="${profileImage}" alt="Profile" />
            <div>
              <p class="user-name">${displayName}</p>
            </div>
          </div>

          <textarea
            name="text"
            class="styled-textarea"
            placeholder="What's on your mind, ${displayName}?"
          ></textarea>

          <div class="preview-grid">
            ${this.previews.map(
              (p, i) => html`
                <div class="preview-thumb">
                  <img src="${p.url}" alt="${p.name}" />
                  <span
                    class="remove-button"
                    @click=${() => this.removeImage(i)}
                    >×</span
                  >
                </div>
              `
            )}
          </div>

          <input
            id="imageUpload"
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            @change=${this.handleFileChange}
          />

          <div class="footer-actions">
            <p>Add to your post</p>
            <div class="action-icons">
              <label for="imageUpload" title="Add images" class="action-icon">
                <sl-icon name="image" style="cursor: pointer;"></sl-icon>
              </label>
              <sl-icon
                name="person-plus"
                class="disabled-icon action-icon"
              ></sl-icon>
              <sl-icon
                name="emoji-smile"
                class="disabled-icon action-icon"
              ></sl-icon>
              <sl-icon
                name="geo-alt"
                class="disabled-icon action-icon"
              ></sl-icon>
              <sl-icon
                name="file-earmark-play"
                class="disabled-icon action-icon"
              ></sl-icon>
            </div>
          </div>

          <sl-button type="submit" class="post-button" variant="neutral" block
            >Post</sl-button
          >
        </form>
      </sl-dialog>
    `;
  }
}

customElements.define('popin-poster', PopinPoster);
